import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import * as API from '../../API';
import FileLockDialog from '../../Components/FileLockDialog/FileLockDialog';
import NotAllowedDialog from '../../Components/NotAllowedDialog/NotAllowedDialog';
import { useFilingCabinetContext } from '../../Contexts/Authenticated/FilingCabinetContext';
import { type FileLockData } from '../../Models/FileLockData';
import RazorView from './RazorView';

export interface FilingCabinetRazorViewProps {
    dataPath: string;
    filingCabinetKey: number;
    path: string;
}

export interface RazorState {
    title: string;
    currentHTML: string;
    isLocked: boolean;
}

const useFilingCabinetData = (filingCabinetKey: number, dataUrl: string, onNotAllowed?: Function): [string | undefined, Function] => {
    const [initialHTMLContent, setInitialHTMLContent] = useState<string>();
    const { getWithID } = useFilingCabinetContext();

    const loadData = async (isReload: boolean): Promise<string> => {
        if (isReload) {
            return await API.general.getRazorView(dataUrl) as string;
        }

        const dataInFilingCabinet = getWithID(filingCabinetKey);
        if (dataInFilingCabinet?.state && dataInFilingCabinet.state !== '') { // If we set the state to '' somewhere and we still use the same fc key we would trigger a reload
            return dataInFilingCabinet.state;
        }

        return await API.general.getRazorView(dataUrl) as string;
    };

    const load = async (isReload: boolean): Promise<void> => {
        const allowUrl = dataUrl.startsWith('/') ? dataUrl : `/${dataUrl}`;
        const canOpen = await API.general.checkIfAllowedToOpen(allowUrl);
        if (!canOpen.Data?.AllOk) {
            onNotAllowed?.();
            return;
        }

        void loadData(isReload)
            .then((data) => {
                try {
                    const error = JSON.parse(data);
                    if (error.Locks?.LockedBy) {
                        window.showLockedFileDialog(error.Locks, window.location.href);
                    }
                }
                catch (e) {
                    setInitialHTMLContent(data);
                }
            });
    };

    useEffect(() => {
        void load(false);
    }, [filingCabinetKey]);

    return [initialHTMLContent, load];
};

const getStateFromNode = (razorRootNode: HTMLDivElement): RazorState => {
    const contentHTML = razorRootNode.innerHTML;
    let title;
    if (razorRootNode.querySelectorAll('.patientBackground ').length > 0) {
        title = razorRootNode.querySelector('.patientBackground')?.firstChild?.nodeValue ?? '';
    }
    else {
        title = razorRootNode.querySelector('h2')?.firstChild?.nodeValue ?? '';
    }

    const locked = razorRootNode.querySelector('#lockedIsLocked')?.innerHTML === 'true';

    return {
        title,
        currentHTML: contentHTML,
        isLocked: locked,
    };
};

const createMutationObserver = (cb: Function): MutationObserver => {
    return new MutationObserver(debounce((records: MutationRecord[]): void => {
        // We are intentionally ignoring state updates when a dialog is open (issue with Razor/JQuery pages) or when all records are updating a dialog. This should NOT be changed as long as we have Razor pages with dialogs.
        const anyOpenDialog = Array.from(document.querySelectorAll('.ui-dialog')).some((x) => (x as HTMLElement).style.display == 'block');
        if (anyOpenDialog) {
            return;
        }
        cb(false);
    }, window.appConfig.UserConfigurableConfig.FilingCabinetDebounceMs)); // Be careful: setting this value too high might result in the state not saving because the user navigated away too quickly!
};

const initSpeechStuff = (): void => {
    if (window.pollG2Timeout) {
        window.clearTimeout(window.pollG2Timeout);
    }
    window.g2SpeechAlreadyOpened = false;
};

const FilingCabinetRazorView = ({
    dataPath,
    path,
    filingCabinetKey,
}: FilingCabinetRazorViewProps): React.ReactElement => {
    const navigate = useNavigate();
    const { add, save, removeActive } = useFilingCabinetContext();
    const [initialHTMLContent, loadData] = useFilingCabinetData(filingCabinetKey, dataPath, () => setShowNotAllowed(true));
    const razorViewRef = useRef<HTMLDivElement>(null);
    const [lockDialogOpen, setLockDialogOpen] = useState(false);
    const [lockData, setLockData] = useState<FileLockData | null>(null);
    const [showNotAllowed, setShowNotAllowed] = useState<boolean>(false);

    useEffect(initSpeechStuff, []);

    useEffect(() => {
        return () => {
            if (window.onNavigateForRazor) {
                void window.onNavigateForRazor();
                window.onNavigateForRazor = null;
            }
        };
    }, []);

    const getRazorRootNode = useCallback((): HTMLDivElement => {
        return razorViewRef.current!;
    }, []);

    const saveCurrentRazorState = useCallback((initial: boolean): void => {
        const razorRootNode = getRazorRootNode();
        if (razorRootNode?.innerHTML == null) {
            return;
        }
        const { title, currentHTML, isLocked } = getStateFromNode(razorRootNode);

        const item = {
            ID: filingCabinetKey,
            path,
            active: true,
            title,
            state: currentHTML,
            isLocked,
        };

        if (initial) {
            return add(item);
        }
        save(item);
    }, [path, filingCabinetKey]);

    const mutationObserver = createMutationObserver(saveCurrentRazorState);

    useEffect(() => {
        if (razorViewRef.current) {
            mutationObserver.observe(razorViewRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
            });
        }

        return () => {
            mutationObserver.disconnect();
        };
    });

    window.reloadActiveRazorView = () => {
        initSpeechStuff();
        loadData(true);
    };

    // Lock Dialog functionality
    const onOk = (): void => {
        setLockDialogOpen(false);
        setShowNotAllowed(false);

        if (lockData?.doNotNavigate ?? false) {
            return;
        }

        // This was made for the specific case of opening an order in edit mode from worklist
        // This is different from eg. reports because it opens in filing cabinet before checking the lock
        if (lockData?.closeCurrentOnOk) {
            void removeActive(true);
        }
        else {
            navigate(-1);
        }
    };

    const onUnlock = (): void => {
        if (lockData == null) {
            return console.error('FilingCabinetRazorView somehow lost lockData state. Cannot unlock');
        }

        setLockDialogOpen(false);
        void API.locks.stealLock({ LockUID: lockData.ID })
            .then((_) => {
                setLockData(null);
                if (lockData.link) {
                    navigate(lockData.link);
                }
                else {
                    lockData.func();
                }
            });
    };

    window.showLockedFileDialog = (response: any, link: string, func: Function, closeCurrentOnOk?: boolean, doNotNavigate?: boolean): void => {
        setLockData({ ID: response.ID, LockedBy: response.LockedBy, LockedOn: response.LockedOn, LockedOnHost: response.LockedOnHost, Type: response.Type, link, func, closeCurrentOnOk, doNotNavigate });
        setLockDialogOpen(true);
    };

    if (showNotAllowed) {
        return <NotAllowedDialog isOpen={showNotAllowed} onOk={onOk} />;
    }

    return (
        <>
            {lockData && <FileLockDialog isOpen={lockDialogOpen} lockData={lockData} onOk={onOk} onUnlock={onUnlock} />}
            <RazorView
                initialContentHTML={initialHTMLContent}
                razorViewRef={razorViewRef}
                afterSetHTML={() => saveCurrentRazorState(true)}
            />
        </>
    );
};

export default FilingCabinetRazorView;
